'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { getCookie } from 'cookies-next';
import {
  generateTimeExpression,
  getCookieName,
} from '../utils/rate-limit/rate-limit-helpers';

/**
 * The interval to check if the cookie has changed.
 */
const COOKIE_CHECK_INTERVAL = 500;

/**
 * The interval to check if the limit has changed.
 */
const RETRY_IN_RECALC_INTERVAL = 1000;

type ChangeListener = (limited: boolean) => void;

export type RateLimitHookReturnValue = {
  limited: boolean;
  retryIn: number;
  retryInExpression: string;
  onChange: (listener: ChangeListener) => void;
};

/**
 * Hook to check rate limit status of a given rate limiter.
 */
export const useRateLimit = (name: string): RateLimitHookReturnValue => {
  let [lastDocumentCookie, setLastDocumentCookie] = useState<string>();
  let [cookieValue, setCookieValue] = useState<string>();
  let [retryIn, setRetryIn] = useState<number>();

  let checkCookieTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  let countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  let limitedRef = useRef<boolean | null>(null);
  let changeListener = useRef<ChangeListener | null>(null);

  let cookieName = getCookieName(name);
  let limited = typeof retryIn !== 'undefined' && retryIn > 0;

  // Let's set up a listener to check if the cookie has changed.
  let checkCookieChange = () => {
    let currentCookies = document.cookie;

    if (currentCookies !== lastDocumentCookie) {
      let cookieValue = getCookie(cookieName);

      if (typeof cookieValue !== 'string') {
        cookieValue = undefined;
      }

      setLastDocumentCookie(currentCookies);
      setCookieValue(cookieValue);
    }

    checkCookieTimeoutRef.current = setTimeout(
      checkCookieChange,
      COOKIE_CHECK_INTERVAL,
    );
  };

  // Kick off the cookie change listener.
  // Once the cookie async api is available, we can use that instead.
  useEffect(() => {
    checkCookieChange();
    return () => {
      if (checkCookieTimeoutRef.current) {
        clearTimeout(checkCookieTimeoutRef.current);
      }
    };
  }, []);

  // Let's set up an interval to check & update the retryIn value, so consumers get a live update.
  useEffect(() => {
    countdownIntervalRef.current = setInterval(() => {
      let calculatedRetryIn = getDifferenceInSeconds(cookieValue) ?? 0;

      if (calculatedRetryIn <= 0) {
        countdownIntervalRef.current &&
          clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }

      setRetryIn(calculatedRetryIn);
    }, RETRY_IN_RECALC_INTERVAL);

    return () => {
      countdownIntervalRef.current &&
        clearInterval(countdownIntervalRef.current);
    };
  }, [cookieValue]);

  // Let's set up a watcher of the limited state, so we can notify the consumer when it changes.
  useEffect(() => {
    // If the retryIn value is not set, or the limited state has not changed, we don't need to notify the consumer.
    // The retryIn value is not set when on initial page load: Avoid notifying the consumer on initial page load.
    if (typeof retryIn === 'undefined' || limitedRef.current === limited) {
      return;
    }

    // We've a valid retryIn value and now we need to assign the initial limited state to the ref.
    if (typeof limitedRef.current === 'undefined') {
      limitedRef.current = limited;

      // We return here, as we don't want to notify the consumer on initial page load.
      return;
    }

    // We arrived here, because the limited state has changed.
    // Let's save the new limited state to the ref.
    limitedRef.current = limited;

    // Notify the consumer about the change.
    if (changeListener.current) {
      changeListener.current(limited);
    }
  }, [limited, retryIn]);

  useEffect(() => {
    return () => {
      changeListener.current = null;
    };
  }, []);

  let onChange = useCallback(
    (listener: ChangeListener) => (changeListener.current = listener),
    [],
  );

  return {
    limited,
    retryIn: retryIn ?? 0,
    retryInExpression: generateTimeExpression(retryIn ?? 0),
    onChange,
  };
};

const getDifferenceInSeconds = (
  cookieValue: string | undefined,
): number | undefined => {
  if (typeof cookieValue === 'undefined') {
    return undefined;
  }

  let asNumber = parseInt(cookieValue, 10);
  let timestamp = Math.floor(new Date().getTime() / 1000);

  return asNumber - timestamp;
};
